import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AlertChannelConfig, AlertChannelKind, AlertRuleConfig, AlertSeverity, AlertThrottle, AlertTrigger, AlertsConfig, RuleAction, RuleCommon } from '@kkp-fraud/shared';
import {
  ALERT_PROBLEM_KEY,
  ALERT_SEVERITIES,
  ALERT_THROTTLES,
  ALERT_TRIGGERS,
  OFFERED_TRIGGERS,
  CHANNEL_KIND_LABEL,
  CHANNEL_PROBLEM_KEY,
  OFFERED_CHANNEL_KINDS,
  SEVERITY_CLASS,
  SEVERITY_LABEL,
  STATUS_CLASS,
  STATUS_LABEL,
  THROTTLE_LABEL,
  TRIGGER_LABEL,
  alertProblems,
  asEmailChannel,
  testRecipients,
  channelDelivery,
  channelProblems,
  channelStatus,
  channelWebhookId,
  channelUsage,
  cloneAlerts,
  conditionSummary,
  defaultParams,
  displayTarget,
  draftWithAlerts,
  effectiveAlerts,
  isOfferedKind,
  nextAlertId,
  ruleStatus,
  type AlertProblem,
  type AlertStatus,
  type AlertsStatus,
  type ChannelProblem,
} from '../alerts';
import { ApiError, COOKIE_MODE, api, webhooksApi } from '../api';
import type { WebhookView } from '../webhooks';
import { WebhooksPanel, webhookErrorText } from './WebhooksPanel';
import { useShell } from '../components/AppLayout';
import { ActionMenu } from '../components/ActionMenu';
import { Dialog } from '../components/Dialog';
import { Field, FieldGroup, useFieldErrors } from '../components/Field';
import { PageHead, SubTabs, Tip } from '../components/PageHead';
import { ACTIONS } from '../fields';
import { t } from '../i18n';
import { canPropose, isViewerOnly } from '../session';
// R8b per-rule-set permissions
import { mayOnSet } from '../session';

/**
 * Alert Management — what fires, who hears about it, through which channel, how often.
 *
 * The rules and their channels are configuration: edited into the draft as
 * `RuleSet.alerts` and released through the same propose/approve path as a rule (see
 * `alerts.ts`). Whether anything is DELIVERED is the alert dispatcher's answer, through the
 * BFF (`GET /api/alerts/status`): the Status columns read it (`ruleStatus` / `channelStatus`),
 * and no answer — password mode, no dispatcher, one that does not respond — is `not connected`.
 * An Editor may send a test email to an email channel's addresses from its row.
 *
 * Email-first: only email and webhook channels are offered. A channel of another kind (from an
 * older document) reads "not supported — switch to email", and an Editor converts it in one
 * click: the channel editor opens as email and asks for the addresses.
 */

type TabId = 'rules' | 'channels' | 'webhooks';
const PRIORITIES: readonly RuleCommon['priority'][] = ['P1', 'P2', 'P3', 'P4'];

function toggleIn<T>(list: readonly T[], value: T, on: boolean): T[] {
  return on ? (list.includes(value) ? [...list] : [...list, value]) : list.filter((v) => v !== value);
}

/** A number input's value: empty or unparsable becomes NaN, which validation refuses. */
const numberOf = (raw: string): number => (raw.trim() === '' ? Number.NaN : Number(raw));
const shownNumber = (n: number): string => (Number.isFinite(n) ? String(n) : '');

function StatusChip({ state, testid, title }: { state: AlertStatus; testid?: string; title?: string }) {
  return (
    <span className={`chip ${STATUS_CLASS[state]}`} data-testid={testid} title={title}>
      {t(STATUS_LABEL[state])}
    </span>
  );
}

/** A channel kind as a picker or chip names it: an unsupported one says so. */
const kindText = (kind: AlertChannelKind): string =>
  isOfferedKind(kind) ? t(CHANNEL_KIND_LABEL[kind]) : t('alert.channel.legacy.option', { kind: t(CHANNEL_KIND_LABEL[kind]) });

/** "Send test" on an email channel's row — to the addresses the row shows, at most `max`. */
function TestButton({ channel, max, busy, disabled, onSend }: { channel: AlertChannelConfig; max: number; busy: boolean; disabled: boolean; onSend: () => void }) {
  // QA-A-13: the same rule as the Status column — a placeholder address is nobody to test.
  const n = testRecipients(channel).length;
  const why = n === 0 ? t('alert.test.no_target.tip') : n > max ? t('alert.test.too_many.tip', { n: max }) : null;
  return (
    <button
      type="button"
      className="btn small"
      data-testid={`channel-test-${channel.id}`}
      disabled={disabled || why !== null}
      title={why ?? t('alert.test.a11y', { name: channel.name })}
      aria-label={t('alert.test.a11y', { name: channel.name })}
      onClick={onSend}
    >
      {busy ? t('alert.test.sending') : t('alert.test.action')}
    </button>
  );
}

const positiveNumber = (n: unknown): boolean => typeof n === 'number' && Number.isFinite(n) && n > 0;

/** The dialog element a form's first invalid control is looked for in (`useFieldErrors().attempt`). */
const dialogRoot = (testid: string): ParentNode | null => document.querySelector(`[data-testid="${testid}"]`);

// ---- alert rule editor --------------------------------------------------------

function AlertRuleDialog({
  initial,
  isNew,
  config,
  ruleOptions,
  onApply,
  onDelete,
  onClose,
}: {
  initial: AlertRuleConfig;
  isNew: boolean;
  config: AlertsConfig;
  ruleOptions: readonly { id: string; name: string }[];
  onApply: (rule: AlertRuleConfig) => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  const [rule, setRule] = useState<AlertRuleConfig>(() => JSON.parse(JSON.stringify(initial)) as AlertRuleConfig);
  const form = useFieldErrors();
  const otherIds = config.rules.filter((r) => isNew || r.id !== initial.id).map((r) => r.id);
  const problems = alertProblems(rule, otherIds, config.channels.map((c) => c.id));
  const title = isNew ? t('alert.new.title') : t('alert.edit.title');
  const says = (p: AlertProblem) => (problems.includes(p) ? t(ALERT_PROBLEM_KEY[p], { id: rule.id.trim() }) : undefined);
  /** A number box's own error: every number an alert rule takes must be greater than 0. */
  const numberError = (n: number) => (positiveNumber(n) ? undefined : t('alert.problem.number'));

  const patch = (p: Partial<AlertRuleConfig>) => setRule((r) => ({ ...r, ...p }) as AlertRuleConfig);
  const setParams = (p: Record<string, unknown>) => setRule((r) => ({ ...r, params: { ...r.params, ...p } }) as AlertRuleConfig);
  const setTrigger = (trigger: AlertTrigger) =>
    setRule((r) => (r.trigger === trigger ? r : ({ ...r, trigger, params: defaultParams(trigger) } as AlertRuleConfig)));

  // UX review P2-5: Apply stays enabled; with a problem it shows every error and focuses the first.
  const apply = () => {
    if (!form.attempt(problems.length === 0, dialogRoot('alert-editor'))) return;
    onApply({ ...rule, id: rule.id.trim(), name: rule.name.trim() });
  };

  /** Rule ids the alert names that the draft no longer has stay listed, so they can be unticked. */
  const rulesListed =
    rule.trigger === 'rule'
      ? [...ruleOptions, ...rule.params.ruleIds.filter((id) => !ruleOptions.some((o) => o.id === id)).map((id) => ({ id, name: '' }))]
      : [];

  return (
    <Dialog
      title={title}
      testid="alert-editor"
      onClose={onClose}
      foot={
        <>
          {!isNew && (
            <button type="button" className="btn danger" onClick={onDelete}>
              {t('alert.delete.action')}
            </button>
          )}
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>
            {t('btn.cancel.label')}
          </button>
          <button type="button" className="btn primary" onClick={apply}>
            {t('alert.apply.action')}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <Field label={t('alert.col.id')} required name="id" form={form} error={form.show('id', says('id_empty') ?? says('id_duplicate'))}>
          <input type="text" className="mono-input" value={rule.id} onChange={(e) => patch({ id: e.target.value.toUpperCase() })} />
        </Field>
        <div style={{ gridColumn: 'span 2' }}>
          <Field label={t('alert.name.label')} required name="name" form={form} error={form.show('name', says('name'))}>
            <input type="text" value={rule.name} onChange={(e) => patch({ name: e.target.value })} />
          </Field>
        </div>
        <Field label={t('alert.enable.label')}>
          <input type="checkbox" role="switch" className="toggle" checked={rule.enabled} onChange={(e) => patch({ enabled: e.target.checked })} />
        </Field>
      </div>

      <div className="form-grid">
        <Field label={t('alert.trigger.label')}>
          <select value={rule.trigger} onChange={(e) => setTrigger(e.target.value as AlertTrigger)}>
            {ALERT_TRIGGERS.filter((tr) => OFFERED_TRIGGERS.includes(tr) || tr === initial.trigger).map((tr) => (
              <option key={tr} value={tr}>
                {t(TRIGGER_LABEL[tr])}
              </option>
            ))}
          </select>
        </Field>

        {rule.trigger === 'decision' && (
          <Field label={t('alert.param.min_priority')}>
            <select
              value={rule.params.minPriority ?? ''}
              onChange={(e) => {
                const minPriority = e.target.value as RuleCommon['priority'] | '';
                setRule((r) => {
                  if (r.trigger !== 'decision') return r;
                  const { minPriority: _old, ...rest } = r.params;
                  return { ...r, params: minPriority ? { ...rest, minPriority } : rest };
                });
              }}
            >
              <option value="">{t('alert.param.min_priority.any')}</option>
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {t('alert.param.min_priority.option', { priority: p })}
                </option>
              ))}
            </select>
          </Field>
        )}

        {rule.trigger === 'degraded' && (
          <>
            <Field label={t('alert.param.percent')} required name="percent" form={form} error={form.show('percent', numberError(rule.params.percent))}>
              <input type="number" min={0} step="any" value={shownNumber(rule.params.percent)} onChange={(e) => setParams({ percent: numberOf(e.target.value) })} />
            </Field>
            <Field label={t('alert.param.window')} required name="window" form={form} error={form.show('window', numberError(rule.params.windowMinutes))}>
              <input type="number" min={0} value={shownNumber(rule.params.windowMinutes)} onChange={(e) => setParams({ windowMinutes: numberOf(e.target.value) })} />
            </Field>
          </>
        )}

        {rule.trigger === 'latency' && (
          <>
            <Field label={t('alert.param.p95')} required name="p95" form={form} error={form.show('p95', numberError(rule.params.p95Ms))}>
              <input type="number" min={0} value={shownNumber(rule.params.p95Ms)} onChange={(e) => setParams({ p95Ms: numberOf(e.target.value) })} />
            </Field>
            <Field label={t('alert.param.minutes')} required name="minutes" form={form} error={form.show('minutes', numberError(rule.params.minutes))}>
              <input type="number" min={0} value={shownNumber(rule.params.minutes)} onChange={(e) => setParams({ minutes: numberOf(e.target.value) })} />
            </Field>
          </>
        )}
      </div>

      {rule.trigger === 'decision' && (
        <FieldGroup legend={t('alert.param.decisions')} required testid="alert-decisions" error={form.show('decisions', says('decisions'))}>
          <div className="row-wrap">
            {ACTIONS.map((a) => (
              <label key={a} className="check">
                <input
                  type="checkbox"
                  checked={rule.params.decisions.includes(a)}
                  onChange={(e) => {
                    form.touch('decisions');
                    setParams({ decisions: toggleIn<RuleAction>(rule.params.decisions, a, e.target.checked) });
                  }}
                />
                <span className="mono small">{a}</span>
              </label>
            ))}
          </div>
        </FieldGroup>
      )}

      {rule.trigger === 'rule' && (
        <FieldGroup legend={t('alert.param.rules')} required testid="alert-rules" error={form.show('rules', says('rules'))}>
          <div className="row-wrap">
            {rulesListed.map((o) => (
              <label key={o.id} className="check" title={o.name}>
                <input
                  type="checkbox"
                  checked={rule.params.ruleIds.includes(o.id)}
                  onChange={(e) => {
                    form.touch('rules');
                    setParams({ ruleIds: toggleIn(rule.params.ruleIds, o.id, e.target.checked) });
                  }}
                />
                <span className="mono small">{o.id}</span>
              </label>
            ))}
          </div>
        </FieldGroup>
      )}

      <div className="small mono muted" data-testid="alert-editor-summary">
        {conditionSummary(rule)}
      </div>

      {/* "Must send somewhere" belongs to the channel boxes as a whole — its error sits under them. */}
      <FieldGroup legend={t('alert.col.channels')} required testid="alert-channels" error={form.show('channels', says('channels') ?? says('channel_unknown'))}>
        <div className="row-wrap">
          {config.channels.map((c) => {
            const ticked = rule.channelIds.includes(c.id);
            // An unsupported channel can be unticked, never newly ticked.
            const locked = !isOfferedKind(c.kind) && !ticked;
            return (
              <label key={c.id} className="check" title={isOfferedKind(c.kind) ? undefined : t('alert.channel.unsupported.tip')}>
                <input
                  type="checkbox"
                  checked={ticked}
                  disabled={locked}
                  data-testid={`alert-channel-pick-${c.id}`}
                  onChange={(e) => {
                    // A group has no "left": changing it is enough to hear that none is ticked any more.
                    form.touch('channels');
                    patch({ channelIds: toggleIn(rule.channelIds, c.id, e.target.checked) });
                  }}
                />
                <span className="small">
                  {c.name} <span className="muted">· {kindText(c.kind)}</span>
                </span>
              </label>
            );
          })}
        </div>
      </FieldGroup>

      <div className="form-grid">
        <Field label={t('alert.severity.label')}>
          <select value={rule.severity} onChange={(e) => patch({ severity: e.target.value as AlertSeverity })}>
            {ALERT_SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {t(SEVERITY_LABEL[s])}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('alert.frequency.label')}>
          <select value={rule.throttle} onChange={(e) => patch({ throttle: e.target.value as AlertThrottle })}>
            {ALERT_THROTTLES.map((th) => (
              <option key={th} value={th}>
                {t(THROTTLE_LABEL[th])}
              </option>
            ))}
          </select>
        </Field>
      </div>
    </Dialog>
  );
}

// ---- channel editor -----------------------------------------------------------

function ChannelDialog({
  initial,
  isNew,
  convert = false,
  config,
  usedBy,
  connections,
  onApply,
  onDelete,
  onClose,
}: {
  initial: AlertChannelConfig;
  isNew: boolean;
  /** Switching an unsupported channel to email: `initial` is already email, and an address is required. */
  convert?: boolean;
  config: AlertsConfig;
  usedBy: readonly string[];
  /** R3 webhooks W1: the admin service's webhook connections; null = not known (password mode, loading). */
  connections: readonly WebhookView[] | null;
  onApply: (ch: AlertChannelConfig) => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  const [ch, setCh] = useState<AlertChannelConfig>({ ...initial });
  const targetRef = useRef<HTMLInputElement>(null);
  const form = useFieldErrors();
  const otherIds = config.channels.filter((c) => isNew || c.id !== initial.id).map((c) => c.id);
  const problems = channelProblems(ch, otherIds, connections ? connections.map((w) => w.id) : null, convert);
  const says = (p: ChannelProblem) => (problems.includes(p) ? t(CHANNEL_PROBLEM_KEY[p], { id: ch.id.trim() }) : undefined);
  const patch = (p: Partial<AlertChannelConfig>) => setCh((c) => ({ ...c, ...p }));
  // R3 webhooks W1: a webhook channel names a connection (id in `webhookId`, and in `target` so
  // either reader finds it); any other kind carries no `webhookId`.
  const applied = (c: AlertChannelConfig): AlertChannelConfig => {
    const { webhookId, ...rest } = c;
    const base = { ...rest, id: c.id.trim(), name: c.name.trim(), target: c.target.trim() };
    return c.kind === 'webhook' && webhookId ? { ...base, target: webhookId, webhookId } : base;
  };
  const picked = ch.kind === 'webhook' ? (channelWebhookId(ch) ?? '') : '';
  const pickedConnection = connections?.find((w) => w.id === picked);
  const apply = () => {
    if (form.attempt(problems.length === 0, dialogRoot('channel-editor'))) onApply(applied(ch));
  };

  return (
    <Dialog
      title={convert ? t('alert.channel.convert.title') : isNew ? t('alert.channel.new.title') : t('alert.channel.edit.title')}
      testid="channel-editor"
      // Switching to email is a question for the addresses: the cursor starts there.
      initialFocus={convert ? targetRef : undefined}
      onClose={onClose}
      foot={
        <>
          {!isNew && (
            <>
              <button type="button" className="btn danger" disabled={usedBy.length > 0} onClick={onDelete}>
                {t('alert.delete.action')}
              </button>
              {usedBy.length > 0 && <Tip>{t('alert.channel.delete.blocked.tip', { ids: usedBy.join(', ') })}</Tip>}
            </>
          )}
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>
            {t('btn.cancel.label')}
          </button>
          <button type="button" className="btn primary" onClick={apply}>
            {t('alert.apply.action')}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <Field label={t('alert.col.id')} required name="id" form={form} error={form.show('id', says('id_empty') ?? says('id_duplicate'))}>
          <input type="text" className="mono-input" value={ch.id} onChange={(e) => patch({ id: e.target.value.toUpperCase() })} />
        </Field>
        <Field label={t('alert.channel.col.kind')} name="kind" form={form} error={form.show('kind', says('kind_unsupported'))}>
          <select value={ch.kind} onChange={(e) => patch({ kind: e.target.value as AlertChannelKind })}>
            {/* An older document's kind stays visible (so the box is not blank) but cannot be chosen. */}
            {!isOfferedKind(ch.kind) && (
              <option value={ch.kind} disabled>
                {kindText(ch.kind)}
              </option>
            )}
            {OFFERED_CHANNEL_KINDS.map((k) => (
              <option key={k} value={k}>
                {t(CHANNEL_KIND_LABEL[k])}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('alert.channel.col.name')} required name="name" form={form} error={form.show('name', says('name'))}>
          <input type="text" value={ch.name} onChange={(e) => patch({ name: e.target.value })} />
        </Field>
      </div>
      {ch.kind === 'webhook' ? (
        <>
          <Field
            label={t('webhooks.channel.connection.label')}
            required
            name="webhook"
            form={form}
            error={form.show('webhook', says('webhook_missing') ?? says('webhook_unknown'))}
          >
            <select data-testid="channel-webhook" value={picked} onChange={(e) => patch({ webhookId: e.target.value || undefined, target: e.target.value })}>
              <option value="">{t('webhooks.channel.connection.none')}</option>
              {picked !== '' && !pickedConnection && <option value={picked}>{picked}</option>}
              {(connections ?? []).map((w) => (
                <option key={w.id} value={w.id}>
                  {w.enabled ? t('webhooks.channel.connection.option', { id: w.id, name: w.name }) : t('webhooks.channel.connection.option_off', { id: w.id, name: w.name })}
                </option>
              ))}
            </select>
          </Field>
          {pickedConnection && !pickedConnection.enabled && <span className="small muted">{t('webhooks.channel.connection.off.hint')}</span>}
        </>
      ) : (
        <Field
          label={t('alert.channel.col.target')}
          required={convert}
          hint={convert ? t('alert.channel.convert.hint') : undefined}
          name="target"
          form={form}
          error={form.show('target', says('target_email') ?? says('target_required'))}
        >
          <input
            type="text"
            spellCheck={false}
            className="mono-input"
            ref={targetRef}
            placeholder={ch.kind === 'email' ? t('alert.channel.target.email.hint') : ''}
            value={ch.target}
            onChange={(e) => patch({ target: e.target.value })}
          />
        </Field>
      )}
    </Dialog>
  );
}

// ---- the screen -----------------------------------------------------------------

export function AlertScreen() {
  const { state, session, settings, showToast, primaryAccess } = useShell();
  const [tab, setTab] = useState<TabId>('rules');
  const [editRule, setEditRule] = useState<{ rule: AlertRuleConfig; isNew: boolean } | null>(null);
  const [editChannel, setEditChannel] = useState<{ channel: AlertChannelConfig; isNew: boolean; convert?: boolean } | null>(null);
  const [delivery, setDelivery] = useState<AlertsStatus | null>(null);
  const [testing, setTesting] = useState<string | null>(null);
  // R3 webhooks W1: the admin service's connections — the Webhooks tab and the channel picker.
  const [webhooks, setWebhooks] = useState<WebhookView[] | null>(COOKIE_MODE ? null : []);
  const [webhooksError, setWebhooksError] = useState<string | null>(null);
  const loadWebhooks = useCallback(() => {
    webhooksApi.list(settings).then(
      (w) => {
        setWebhooks(Array.isArray(w) ? w : []);
        setWebhooksError(null);
      },
      (e: unknown) => {
        setWebhooks([]);
        setWebhooksError(webhookErrorText(e));
      },
    );
  }, [settings]);
  useEffect(() => loadWebhooks(), [loadWebhooks]);
  // QA-A-09: which connections exist and are switched on — a channel on an Off connection sends nothing.
  const connectionStates = useMemo(() => (COOKIE_MODE && webhooks && !webhooksError ? new Map(webhooks.map((w) => [w.id, w.enabled])) : null), [webhooks, webhooksError]);

  useEffect(() => {
    let live = true;
    api
      .alertStatus(settings)
      .then((s) => live && setDelivery(s))
      // No answer is "not connected" — the screen still renders.
      .catch(() => live && setDelivery(null));
    return () => {
      live = false;
    };
  }, [settings]);

  const config = effectiveAlerts(state.draft);
  // R8b: alert rules live in the primary set's document — `edit` on it (publishing them is the
  // release, which needs `publish`). Without a known level the role decides, as before.
  const mayEdit = (primaryAccess !== undefined ? mayOnSet(session, primaryAccess, 'edit') : !isViewerOnly(session)) && state.draft !== null;
  // Real email leaves the platform: signed-in Editors only (the BFF's CanSendTestAlert), and
  // only while the dispatcher would send or dry-run it.
  const mayTest = session !== null && canPropose(session) && delivery?.testEnabled === true;

  const sendTest = async (ch: AlertChannelConfig) => {
    const to = testRecipients(ch);
    setTesting(ch.id);
    try {
      const r = await api.sendTestAlert(settings, { channelId: ch.id, channelName: ch.name, to });
      showToast(t(r.status === 'dry_run' ? 'alert.test.dry_run' : 'alert.test.sent', { n: r.recipients ?? to.length }));
    } catch (e) {
      const detail = e instanceof ApiError && e.status === 429 ? t('alert.test.rate_limited') : (e as Error).message;
      showToast(t('alert.test.failed', { detail }), 'error');
    } finally {
      setTesting(null);
    }
  };
  const usage = useMemo(() => channelUsage(config), [config]);
  const channelById = useMemo(() => new Map(config.channels.map((c) => [c.id, c])), [config]);
  const ruleOptions = useMemo(
    () => (state.draft ? [...state.draft.ruleBased, ...state.draft.patternBased].map((r) => ({ id: r.id, name: r.name })) : []),
    [state.draft],
  );

  /** Every change goes through here — see `draftWithAlerts` for why it may not write at all. */
  const write = (next: AlertsConfig) => {
    if (!state.draft) return;
    const out = draftWithAlerts(state.loaded, state.draft, next);
    if (out === null) return;
    if (out.alerts === undefined) state.setDraft(out);
    else state.patchRuleSet({ alerts: next });
  };

  const toggle = (id: string, enabled: boolean) => {
    const next = cloneAlerts(config);
    next.rules = next.rules.map((r) => (r.id === id ? { ...r, enabled } : r));
    write(next);
  };

  const applyRule = (rule: AlertRuleConfig, originalId: string | null) => {
    const next = cloneAlerts(config);
    const at = originalId === null ? -1 : next.rules.findIndex((r) => r.id === originalId);
    if (at >= 0) next.rules[at] = rule;
    else next.rules.push(rule);
    write(next);
    setEditRule(null);
  };

  const deleteRule = (id: string) => {
    const next = cloneAlerts(config);
    next.rules = next.rules.filter((r) => r.id !== id);
    write(next);
    setEditRule(null);
  };

  const applyChannel = (ch: AlertChannelConfig, originalId: string | null) => {
    const next = cloneAlerts(config);
    const at = originalId === null ? -1 : next.channels.findIndex((c) => c.id === originalId);
    if (at >= 0) next.channels[at] = ch;
    else next.channels.push(ch);
    // A renamed channel keeps every alert that sends to it.
    if (originalId !== null && originalId !== ch.id) {
      next.rules = next.rules.map((r) => ({ ...r, channelIds: r.channelIds.map((c) => (c === originalId ? ch.id : c)) }));
    }
    write(next);
    setEditChannel(null);
  };

  const deleteChannel = (id: string) => {
    if ((usage.get(id) ?? []).length > 0) return;
    const next = cloneAlerts(config);
    next.channels = next.channels.filter((c) => c.id !== id);
    write(next);
    setEditChannel(null);
  };

  const addRule = () =>
    setEditRule({
      isNew: true,
      rule: {
        id: nextAlertId('AL', config.rules.map((r) => r.id)),
        name: '',
        enabled: true,
        trigger: 'decision',
        params: { decisions: ['DECLINE'] },
        channelIds: [],
        severity: 'medium',
        throttle: 'immediate',
      },
    });

  const addChannel = () =>
    setEditChannel({ isNew: true, channel: { id: nextAlertId('CH', config.channels.map((c) => c.id)), kind: 'email', name: '', target: '' } });

  /** One click from "not supported": the editor opens as email and asks for the addresses. */
  const convertChannel = (c: AlertChannelConfig) => setEditChannel({ isNew: false, convert: true, channel: asEmailChannel(c) });

  const enabledCount = config.rules.filter((r) => r.enabled).length;

  return (
    <>
      <PageHead title={t('screen.alert.title')} lead={t('alert.lead')} />

      <SubTabs
        label={t('screen.alert.title')}
        active={tab}
        onSelect={(id) => setTab(id as TabId)}
        tabs={[
          { id: 'rules', label: t('alert.rules.title'), badge: config.rules.length },
          { id: 'channels', label: t('alert.channels.title'), badge: config.channels.length },
          { id: 'webhooks', label: t('webhooks.title'), badge: webhooks?.length ?? null },
        ]}
      />

      {tab === 'webhooks' && <WebhooksPanel config={config} webhooks={webhooks} error={webhooksError} onChanged={loadWebhooks} />}

      {tab === 'rules' && (
        <section className="card">
          <header className="card-head">
            <h2>{t('alert.rules.title')}</h2>
            <span className="chip mute" data-testid="alert-count">
              {t('alert.rules.count', { n: config.rules.length, on: enabledCount })}
            </span>
            <span className="spacer" />
            {mayEdit && (
              <button type="button" className="btn small" data-testid="alert-add" onClick={addRule}>
                {t('alert.add.action')}
              </button>
            )}
          </header>
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 70 }}>{t('alert.col.id')}</th>
                  <th>{t('alert.name.label')}</th>
                  <th style={{ width: 240 }}>{t('alert.col.channel')}</th>
                  <th style={{ width: 80 }}>{t('alert.col.severity.head')}</th>
                  <th style={{ width: 140 }}>{t('alert.col.how_often')}</th>
                  <th style={{ width: 120 }}>
                    {t('col.status.label')} <Tip align="right">{t('alert.status.not_connected.tip')}</Tip>
                  </th>
                  <th style={{ width: 62 }}>{t('state.enabled.label')}</th>
                  {mayEdit && <th style={{ width: 44 }} aria-label={t('alert.col.actions')} />}
                </tr>
              </thead>
              <tbody>
                {config.rules.length === 0 && (
                  <tr>
                    <td colSpan={mayEdit ? 8 : 7} className="empty" role="status">
                      {t('alert.rules.empty')}
                    </td>
                  </tr>
                )}
                {config.rules.map((a) => (
                  <tr
                    key={a.id}
                    data-testid={`alert-${a.id}`}
                    className={`${mayEdit ? 'clickable' : ''}${a.enabled ? '' : ' disabled'}`}
                    onClick={mayEdit ? () => setEditRule({ rule: a, isNew: false }) : undefined}
                  >
                    <td className="mono">{a.id}</td>
                    <td>
                      {/* UX review P1-5: the name is the row's keyboard way in (Enter opens), where the row opens at all. */}
                      <div>
                        {mayEdit ? (
                          <button
                            type="button"
                            className="rowlink"
                            data-testid={`alert-open-${a.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditRule({ rule: a, isNew: false });
                            }}
                          >
                            {a.name || a.id}
                          </button>
                        ) : (
                          a.name
                        )}
                      </div>
                      <div className="small muted" title={t(TRIGGER_LABEL[a.trigger])} data-testid={`alert-cond-${a.id}`}>
                        {conditionSummary(a)}
                      </div>
                    </td>
                    <td>
                      <div className="stack-chips">
                        {a.channelIds.map((id) => {
                          const c = channelById.get(id);
                          const unsupported = c !== undefined && !isOfferedKind(c.kind);
                          return (
                            <span
                              key={id}
                              className={`chip${c ? (unsupported ? ' warn' : '') : ' bad'}`}
                              data-testid={`alert-${a.id}-channel-${id}`}
                              title={unsupported ? t('alert.channel.unsupported.tip') : undefined}
                            >
                              {c ? `${c.name} · ${kindText(c.kind)}` : id}
                            </span>
                          );
                        })}
                      </div>
                    </td>
                    <td>
                      <span className={`chip ${SEVERITY_CLASS[a.severity] ?? ''}`}>{t(SEVERITY_LABEL[a.severity])}</span>
                    </td>
                    <td className="small">{t(THROTTLE_LABEL[a.throttle])}</td>
                    <td>
                      <StatusChip state={ruleStatus(a, config, delivery, connectionStates)} testid={`alert-status-${a.id}`} />
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        role="switch"
                        className="toggle"
                        checked={a.enabled}
                        disabled={!mayEdit}
                        aria-label={t('alert.toggle.a11y', { id: a.id })}
                        onChange={(e) => toggle(a.id, e.target.checked)}
                      />
                    </td>
                    {/* UX review P2-10: row actions behind the row's •••, Edit first, Delete last and set apart. */}
                    {mayEdit && (
                      <td onClick={(e) => e.stopPropagation()}>
                        <ActionMenu
                          variant="row"
                          label={t('row.menu.a11y', { id: a.id })}
                          items={[
                            { label: t('row.edit.label'), onSelect: () => setEditRule({ rule: a, isNew: false }) },
                            { label: t('alert.delete.action'), onSelect: () => deleteRule(a.id), separated: true },
                          ]}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'channels' && (
        <section className="card">
          <header className="card-head">
            <h2>{t('alert.channels.title')}</h2>
            <span className="spacer" />
            {mayEdit && (
              <button type="button" className="btn small" data-testid="channel-add" onClick={addChannel}>
                {t('alert.channel.add.action')}
              </button>
            )}
          </header>
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 70 }}>{t('alert.col.id')}</th>
                  <th style={{ width: 200 }}>{t('alert.channel.col.kind')}</th>
                  <th>{t('alert.channel.col.name')}</th>
                  <th>{t('alert.channel.col.target')}</th>
                  <th style={{ width: 200 }}>{t('alert.channel.col.used')}</th>
                  <th style={{ width: 130 }}>
                    {t('col.status.label')} <Tip align="right">{t('alert.status.not_connected.tip')}</Tip>
                  </th>
                  {mayTest && <th style={{ width: 120 }} aria-label={t('alert.test.col')} />}
                  {mayEdit && <th style={{ width: 44 }} aria-label={t('alert.col.actions')} />}
                </tr>
              </thead>
              <tbody>
                {config.channels.length === 0 && (
                  <tr>
                    <td colSpan={6 + (mayTest ? 1 : 0) + (mayEdit ? 1 : 0)} className="empty" role="status">
                      {t('alert.channels.empty')}
                    </td>
                  </tr>
                )}
                {config.channels.map((c) => {
                  const used = usage.get(c.id) ?? [];
                  const target = displayTarget(c);
                  return (
                    <tr
                      key={c.id}
                      data-testid={`channel-${c.id}`}
                      className={mayEdit ? 'clickable' : ''}
                      onClick={mayEdit ? () => setEditChannel({ channel: c, isNew: false }) : undefined}
                    >
                      <td className="mono">{c.id}</td>
                      <td className="small">{t(CHANNEL_KIND_LABEL[c.kind])}</td>
                      <td>
                        {mayEdit ? (
                          <button
                            type="button"
                            className="rowlink"
                            data-testid={`channel-open-${c.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditChannel({ channel: c, isNew: false });
                            }}
                          >
                            {c.name || c.id}
                          </button>
                        ) : (
                          c.name
                        )}
                      </td>
                      <td className="small mono" data-testid={`channel-target-${c.id}`}>
                        {target || <span className="muted">—</span>}
                      </td>
                      <td>
                        {used.length === 0 ? (
                          <span className="muted">—</span>
                        ) : (
                          <div className="row-wrap">
                            {used.map((id) => (
                              <span key={id} className="chip mono">
                                {id}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td onClick={isOfferedKind(c.kind) ? undefined : (e) => e.stopPropagation()}>
                        {isOfferedKind(c.kind) ? (
                          <StatusChip state={channelStatus(c, delivery, connectionStates)} testid={`channel-status-${c.id}`} />
                        ) : (
                          <div className="row-wrap">
                            <span className={`chip ${STATUS_CLASS.unsupported}`} data-testid={`channel-status-${c.id}`} title={t('alert.channel.unsupported.tip')}>
                              {t('alert.channel.unsupported.chip')}
                            </span>
                            {mayEdit && (
                              <button
                                type="button"
                                className="btn small"
                                data-testid={`channel-convert-${c.id}`}
                                aria-label={t('alert.channel.convert.a11y', { name: c.name || c.id })}
                                onClick={() => convertChannel(c)}
                              >
                                {t('alert.channel.convert.action')}
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                      {mayTest && (
                        <td onClick={(e) => e.stopPropagation()}>
                          {c.kind === 'email' && channelDelivery(delivery, c.kind) !== 'not_connected' && (
                            <TestButton
                              channel={c}
                              max={delivery?.testMaxRecipients ?? 5}
                              busy={testing === c.id}
                              disabled={testing !== null}
                              onSend={() => void sendTest(c)}
                            />
                          )}
                        </td>
                      )}
                      {mayEdit && (
                        <td onClick={(e) => e.stopPropagation()}>
                          <ActionMenu
                            variant="row"
                            label={t('row.menu.a11y', { id: c.id })}
                            items={[
                              { label: t('row.edit.label'), onSelect: () => setEditChannel({ channel: c, isNew: false }) },
                              // A channel an alert sends to cannot go: the editor's Delete says which alerts use it.
                              { label: t('alert.delete.action'), onSelect: () => deleteChannel(c.id), disabled: used.length > 0, separated: true },
                            ]}
                          />
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {editRule && (
        <AlertRuleDialog
          initial={editRule.rule}
          isNew={editRule.isNew}
          config={config}
          ruleOptions={ruleOptions}
          onApply={(r) => applyRule(r, editRule.isNew ? null : editRule.rule.id)}
          onDelete={() => deleteRule(editRule.rule.id)}
          onClose={() => setEditRule(null)}
        />
      )}

      {editChannel && (
        <ChannelDialog
          initial={editChannel.channel}
          isNew={editChannel.isNew}
          convert={editChannel.convert === true}
          config={config}
          usedBy={editChannel.isNew ? [] : (usage.get(editChannel.channel.id) ?? [])}
          connections={COOKIE_MODE ? webhooks : null}
          onApply={(c) => applyChannel(c, editChannel.isNew ? null : editChannel.channel.id)}
          onDelete={() => deleteChannel(editChannel.channel.id)}
          onClose={() => setEditChannel(null)}
        />
      )}
    </>
  );
}
